'use client';

/**
 * components/ThemeToggle.tsx — bascule jour/nuit de l'application.
 *
 * Pose `data-theme="light|dark"` sur <html>. Tout le thème se branche sur cet
 * attribut (app/globals.css), jamais sur une classe portée par un
 * composant : les pages applicatives sont plein écran et montent des overlays en
 * portail, qui ne verraient pas un wrapper local.
 *
 * ponytail: pas de script anti-flash dans le <head>. Plafond : un demi-battement
 * de thème par défaut au chargement. Montée : script inline dans le layout
 * racine : FAIT le 13/08 (voir app/layout.tsx), le thème est posé avant
 * l'hydratation et le demi-battement a disparu.
 */

import { useSyncExternalStore } from 'react';
import { themeStore, signalerChoixUtilisateur } from './theme';
import { topBanner } from './topBanner';
import { useLandscape } from '@/hooks/useLandscape';

export default function ThemeToggle() {
  const theme = useSyncExternalStore(themeStore.subscribe, themeStore.lire, themeStore.serveur);
  // En paysage, l'encart occupe le côté du bord bas de l'appareil : la bascule
  // se posait DESSUS. Elle passe donc du côté de la carte — le seul endroit où
  // elle est visible et cliquable.
  const { landscape, side } = useLandscape();
  const onLeft = landscape && side === 'right';
  // La bascule reste fixe en haut à droite, MÊME endroit qu'une notification
  // de compte ou l'encart de dispatch — elle passait dessus, recouvrant leur
  // croix de fermeture (signalé le 25/08). Elle descend d'autant tant qu'un
  // bandeau occupe le haut de l'écran, transition CSS à l'appui : l'effet
  // recherché est qu'elle se sente POUSSÉE vers le bas par ce qui apparaît.
  const décalage = useSyncExternalStore(topBanner.subscribe, topBanner.lire, topBanner.serveur);

  // AUCUN effet ne repose `data-theme` ici, et c'est le correctif du 14/08 :
  // le thème choisi ne survivait pas à un rechargement, il fallait rebasculer
  // à chaque retour dans l'application.
  //
  // L'enchaînement exact : le script du layout pose `dark` avant l'hydratation ;
  // React hydrate en revanche avec l'instantané SERVEUR (`light`, il n'a pas
  // accès à localStorage) ; l'effet [theme] partait donc une première fois avec
  // `light` et ÉCRASAIT le `dark` du script. Quand `useSyncExternalStore`
  // vérifiait ensuite l'instantané client, `lire()` interrogeait un DOM déjà
  // repeint en clair — le choix de l'utilisateur était perdu sans laisser de
  // trace, et localStorage, lui, gardait bien `dark`.
  //
  // Il n'y avait rien à ajouter : `ecrire()` pose l'attribut au moment du clic,
  // le script du layout le pose au chargement. Ces deux-là suffisent, et l'effet
  // ne pouvait qu'arriver après eux, avec une valeur plus vieille que la leur.

  const jour = theme === 'light';

  return (
    <button
      type="button"
      onClick={() => { signalerChoixUtilisateur(); themeStore.ecrire(jour ? 'dark' : 'light'); }}
      aria-label={jour ? 'Passer en mode nuit' : 'Passer en mode jour'}
      title={jour ? 'Mode jour' : 'Mode nuit'}
      style={{ top: `calc(env(safe-area-inset-top) + 12px + ${décalage}px)`, [onLeft ? 'left' : 'right']: 12 }}
      className="sky-btn fixed z-[9999] w-9 h-9 rounded-full flex items-center justify-center
                 active:scale-90 transition-[top,transform] duration-300 ease-out"
    >
      {/* Les étoiles ne vivent QUE la nuit — de jour elles seraient des poussières
          blanches sur un ciel bleu. Positions et retards en dur : six points
          placés à la main valent mieux qu'un aléatoire qui, sur 36 px, finit
          régulièrement par les empiler. */}
      {jour && (
        <span aria-hidden className="absolute inset-0 pointer-events-none">
          {[{ t: 15, l: 8, d: .3 }, { t: 60, l: 75, d: .8 }, { t: 35, l: 50, d: 1.2 },
            { t: 70, l: 20, d: .6 }, { t: 25, l: 85, d: 1.5 }, { t: 80, l: 45, d: .1 }].map((st, i) => (
            <span key={i} className="sky-star"
              style={{ top: `${st.t}%`, left: `${st.l}%`, animationDelay: `${st.d}s` }} />
          ))}
        </span>
      )}
      {jour ? (
        // De JOUR, la LUNE et le ciel nocturne : le bouton montre la destination.
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sky-moon relative">
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
      ) : (
        // Disque PLEIN et rayons épais : au trait, le halo se lisait comme une
        // ombre autour d'un contour au lieu d'une lumière qui sort de l'astre.
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2.4" strokeLinecap="round" className="sky-sun relative">
          <circle cx="12" cy="12" r="4.2" fill="currentColor" stroke="none" />
          <path d="M12 2.2v2.4M12 19.4v2.4M4.6 4.6l1.7 1.7M17.7 17.7l1.7 1.7M2.2 12h2.4M19.4 12h2.4M4.6 19.4l1.7-1.7M17.7 6.3l1.7-1.7" />
        </svg>
      )}
    </button>
  );
}
